/**
 * IncassoCard
 *
 * Kanban card for a single incasso dossier (one card = one debtor).
 * Memoized with a custom comparison function to prevent unnecessary re-renders.
 *
 * Layout:
 *  Row 1: Beoordeling dot + label · Amount (red) · Days overdue · SLA warning
 *  Row 2: Customer name
 *  Row 3: Vennootschap
 *  Row 4: Building · Unit
 *  Row 5: Contact (phone, email)
 *  ─────────────────
 *  Row 6: Action count · Legal count · Payment plan badge · Invoice count
 *  Row 7: Legal procedure info (if active)
 *  Row 8: Notes preview (if set)
 *  ─────────────────
 *  Row 9: Snooze indicator (if snoozed)
 */

import { memo } from 'react';
import { Card, CardContent } from 'components/ui/card';
import {
  Building,
  Cube,
  ClipboardText,
  Scales,
  ListChecks,
  FileText,
  ChatText,
  Warning,
  Phone,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { IncassoTicket, IncassoBeoordeling } from '../shared/incassoTypes';
import { BeoordelingPopover } from './BeoordelingPopover';
import { SnoozeIndicator } from './SnoozeIndicator';

interface IncassoCardProps {
  ticket: IncassoTicket;
  onClick: () => void;
  onBeoordelingChange?: (beoordeling: IncassoBeoordeling | null, reden?: string) => void;
  fontSize?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatEuro = (amount: number): string =>
  new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const getDaysOverdueColor = (days: number): string => {
  if (days >= 90) return 'text-red-700';
  if (days >= 30) return 'text-orange-700';
  return 'text-neutral-secondary-mid';
};

const BEOORDELING_SHORT_LABELS: Record<IncassoBeoordeling, string> = {
  [IncassoBeoordeling.KOMT_IN_ORDE]: 'Wellicht in orde',
  [IncassoBeoordeling.NIET_BEREIKBAAR]: 'Onbereikbaar',
  [IncassoBeoordeling.KOMT_NIET_IN_ORDE]: 'Niet in orde',
};

const BEOORDELING_LABEL_COLORS: Record<IncassoBeoordeling, string> = {
  [IncassoBeoordeling.KOMT_IN_ORDE]: 'text-green-700',
  [IncassoBeoordeling.NIET_BEREIKBAAR]: 'text-amber-700',
  [IncassoBeoordeling.KOMT_NIET_IN_ORDE]: 'text-red-700',
};

// ─── Component ────────────────────────────────────────────────────────────────

const IncassoCardComponent = ({ ticket, onClick, onBeoordelingChange, fontSize = 12 }: IncassoCardProps) => {
  const metaSize = Math.max(fontSize - 2, 10);
  const iconSize = Math.max(fontSize - 2, 10);

  // Safe defaults for enriched fields
  const isSnoozed = ticket.isSnoozed ?? (
    ticket.snoozeUntil ? new Date(ticket.snoozeUntil) > new Date() : false
  );
  const isCritical = ticket.beoordeling === IncassoBeoordeling.KOMT_NIET_IN_ORDE;
  const totalDebt = ticket.totalDebt ?? 0;
  const maxDaysOverdue = ticket.maxDaysOverdue ?? 0;
  const daysInCurrentPhase = ticket.daysInCurrentPhase ?? 0;
  const incassoActionCount = ticket.incassoActionCount ?? 0;
  const legalProcedureCount = ticket.legalProcedureCount ?? 0;
  const activeLegalProcedures = ticket.activeLegalProcedures ?? 0;
  const activePaymentPlans = ticket.activePaymentPlans ?? 0;
  const invoiceCount = ticket.invoiceCount ?? 0;
  const phoneNumber = ticket.contact?.phone_mobile || ticket.contact?.phone_direct || ticket.company?.phone_direct;
  const email = ticket.contact?.email || ticket.company?.email;

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all duration-200 overflow-hidden will-change-auto',
        'hover:shadow-md hover:border-moss-primary/30',
        // Priority: critical > legal > payment plan > snoozed
        isCritical && 'ring-1 ring-red-200 border-l-4 border-l-red-500',
        !isCritical && activeLegalProcedures > 0 && 'bg-red-50/50 border-l-2 border-l-red-400',
        !isCritical && activeLegalProcedures === 0 && activePaymentPlans > 0 && 'bg-blue-50/50 border-l-2 border-l-blue-400',
        !isCritical && activeLegalProcedures === 0 && activePaymentPlans === 0 && isSnoozed && 'opacity-60 bg-neutral-50 border-dashed',
        !isCritical && activeLegalProcedures === 0 && activePaymentPlans === 0 && !isSnoozed && 'border-neutral-200',
      )}
      onClick={onClick}
    >
      <CardContent className="p-2 space-y-1.5 overflow-hidden">
        {/* Row 1: Beoordeling dot+label · Amount · Days overdue · SLA warning */}
        <div className="flex items-center gap-1.5 min-w-0">
          {/* Beoordeling popover — stopPropagation to prevent card click */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 shrink-0">
            <BeoordelingPopover
              value={ticket.beoordeling}
              reden={ticket.beoordelingReden}
              onChange={(v, reden) => onBeoordelingChange?.(v, reden)}
            />
            {ticket.beoordeling && (
              <span
                className={cn('font-medium', BEOORDELING_LABEL_COLORS[ticket.beoordeling])}
                style={{ fontSize: `${metaSize}px` }}
              >
                {BEOORDELING_SHORT_LABELS[ticket.beoordeling]}
              </span>
            )}
          </div>

          <span
            className="font-bold text-red-700 shrink-0"
            style={{ fontSize: `${fontSize}px` }}
          >
            {formatEuro(totalDebt)}
          </span>

          <span
            className={cn('ml-auto shrink-0 font-medium', getDaysOverdueColor(maxDaysOverdue))}
            style={{ fontSize: `${metaSize}px` }}
          >
            {maxDaysOverdue}d
          </span>

          {daysInCurrentPhase > 14 && (
            <Warning
              weight="duotone"
              size={iconSize}
              className="text-amber-500 shrink-0"
              aria-label="SLA-waarschuwing"
            />
          )}
        </div>

        {/* Row 2: Customer name */}
        <p
          className="font-medium text-neutral-900 line-clamp-1 leading-snug"
          style={{ fontSize: `${fontSize}px` }}
        >
          {ticket.odooPartnerDisplayName}
        </p>

        {/* Row 3: Vennootschap */}
        {ticket.cachedCompanyName && (
          <p
            className="text-neutral-secondary-mid line-clamp-1"
            style={{ fontSize: `${metaSize}px` }}
          >
            {ticket.cachedCompanyName}
          </p>
        )}

        {/* Row 4: Building + Unit */}
        {(ticket.buildingName || ticket.cachedBuildingName || ticket.unitLabel || ticket.cachedUnitLabel) && (
          <div className="flex items-center gap-2 min-w-0" style={{ fontSize: `${metaSize}px` }}>
            {(ticket.buildingName || ticket.cachedBuildingName) && (
              <div className="flex items-center gap-0.5 min-w-0">
                <Building weight="duotone" size={iconSize} className="text-moss-primary shrink-0" />
                <span className="font-medium text-moss-primary truncate">
                  {ticket.buildingName || ticket.cachedBuildingName}
                </span>
              </div>
            )}
            {(ticket.unitLabel || ticket.cachedUnitLabel) && (
              <div className="flex items-center gap-0.5 shrink-0">
                <Cube weight="duotone" size={iconSize} className="text-blue-600 shrink-0" />
                <span className="font-medium text-blue-600">{ticket.unitLabel || ticket.cachedUnitLabel}</span>
              </div>
            )}
          </div>
        )}

        {/* Row 5: Contact info (compact) */}
        {(phoneNumber || email) && (
          <div className="flex items-center gap-2 min-w-0" style={{ fontSize: `${metaSize}px` }}>
            {phoneNumber && (
              <div className="flex items-center gap-0.5 text-neutral-secondary-mid shrink-0">
                <Phone weight="duotone" size={iconSize} className="shrink-0" />
                <span className="truncate max-w-[80px]">{phoneNumber}</span>
              </div>
            )}
            {email && (
              <div className="flex items-center gap-0.5 text-neutral-secondary-mid min-w-0">
                <EnvelopeSimple weight="duotone" size={iconSize} className="shrink-0" />
                <span className="truncate">{email}</span>
              </div>
            )}
          </div>
        )}

        {/* Separator */}
        <div className="h-px bg-neutral-100" />

        {/* Row 6: Action/Legal/Plan/Invoice badges */}
        <div
          className="flex items-center gap-2.5 min-w-0 flex-wrap"
          style={{ fontSize: `${metaSize}px` }}
        >
          {/* Incasso actions count */}
          {incassoActionCount > 0 && (
            <div className="flex items-center gap-0.5 text-neutral-secondary-mid shrink-0">
              <ClipboardText weight="duotone" size={iconSize} className="shrink-0" />
              <span className="font-medium">{incassoActionCount}</span>
            </div>
          )}

          {/* Legal procedures */}
          {legalProcedureCount > 0 && (
            <div className={cn(
              'flex items-center gap-0.5 shrink-0',
              activeLegalProcedures > 0 ? 'text-red-700' : 'text-neutral-secondary-mid',
            )}>
              <Scales weight="duotone" size={iconSize} className="shrink-0" />
              <span className="font-medium">{legalProcedureCount}</span>
            </div>
          )}

          {/* Payment plans */}
          {activePaymentPlans > 0 && ticket.paymentPlanProgress && (
            <div className="flex items-center gap-0.5 text-purple-700 shrink-0">
              <ListChecks weight="duotone" size={iconSize} className="shrink-0" />
              <span className="font-medium">{ticket.paymentPlanProgress}</span>
            </div>
          )}

          {/* Invoice count */}
          {invoiceCount > 0 && (
            <div className="flex items-center gap-0.5 text-neutral-secondary-mid shrink-0 ml-auto">
              <FileText weight="duotone" size={iconSize} className="shrink-0" />
              <span className="font-medium">{invoiceCount}</span>
            </div>
          )}
        </div>

        {/* Row 7: Active legal procedure details */}
        {activeLegalProcedures > 0 && ticket.activeLegalReference && (
          <div
            className="flex items-center gap-0.5 text-red-700 min-w-0"
            style={{ fontSize: `${metaSize}px` }}
          >
            <Scales weight="duotone" size={iconSize} className="shrink-0" />
            <span className="truncate">
              {ticket.activeLegalReference}
              {ticket.activeLegalNextDate && (
                <> · Zitting {ticket.activeLegalNextDate}</>
              )}
            </span>
          </div>
        )}

        {/* Row 8: Notes preview */}
        {ticket.notes && (
          <div
            className="flex items-start gap-0.5 text-neutral-secondary-mid min-w-0"
            style={{ fontSize: `${metaSize}px` }}
          >
            <ChatText weight="duotone" size={iconSize} className="shrink-0 mt-0.5" />
            <span className="line-clamp-1 italic truncate">{ticket.notes}</span>
          </div>
        )}

        {/* Separator + Snooze indicator */}
        {isSnoozed && ticket.snoozeUntil && (
          <>
            <div className="h-px bg-neutral-100" />
            <SnoozeIndicator
              snoozeUntil={ticket.snoozeUntil}
              snoozeReason={ticket.snoozeReason}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Memoization ─────────────────────────────────────────────────────────────

export const IncassoCard = memo(IncassoCardComponent, (prev, next) => {
  return (
    prev.ticket.id === next.ticket.id &&
    prev.ticket.phase === next.ticket.phase &&
    prev.ticket.beoordeling === next.ticket.beoordeling &&
    prev.ticket.beoordelingReden === next.ticket.beoordelingReden &&
    prev.ticket.snoozeUntil === next.ticket.snoozeUntil &&
    prev.ticket.totalDebt === next.ticket.totalDebt &&
    prev.ticket.maxDaysOverdue === next.ticket.maxDaysOverdue &&
    prev.ticket.daysInCurrentPhase === next.ticket.daysInCurrentPhase &&
    prev.ticket.isSnoozed === next.ticket.isSnoozed &&
    prev.ticket.notes === next.ticket.notes &&
    prev.ticket.incassoActionCount === next.ticket.incassoActionCount &&
    prev.ticket.legalProcedureCount === next.ticket.legalProcedureCount &&
    prev.ticket.paymentPlanProgress === next.ticket.paymentPlanProgress &&
    prev.ticket.activeLegalProcedures === next.ticket.activeLegalProcedures &&
    prev.ticket.invoiceCount === next.ticket.invoiceCount &&
    prev.ticket.cachedCompanyName === next.ticket.cachedCompanyName &&
    prev.ticket.contact?.phone_mobile === next.ticket.contact?.phone_mobile &&
    prev.ticket.contact?.email === next.ticket.contact?.email &&
    prev.fontSize === next.fontSize
  );
});
